import { Pencil, Pentagon, Trash2 } from 'lucide-react'
import { cn } from '@/lib/cn'
import type { AreaGroup, SubArea } from '@/types/api'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { toneClassForGroup } from './tones'
import styles from './AreaPricingPage.module.css'

interface SubAreasPanelProps {
  subAreas: SubArea[]
  groups: AreaGroup[]
  selectedId: number | null
  disabled: boolean
  onDraw: () => void
  onSelect: (subArea: SubArea) => void
  onEdit: (subArea: SubArea) => void
  onDelete: (subArea: SubArea) => void
}

const AREA_FORMAT = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 })

/** Every sub-area drawn, with its group. Picking one shows it on the map. */
export function SubAreasPanel({
  subAreas,
  groups,
  selectedId,
  disabled,
  onDraw,
  onSelect,
  onEdit,
  onDelete,
}: SubAreasPanelProps) {
  return (
    <Card>
      <CardHeader
        title="Sub-areas"
        description="Drawn on the map. Where two overlap, the smaller one decides."
        actions={
          <Button size="sm" iconLeft={<Pentagon aria-hidden="true" />} onClick={onDraw} disabled={disabled}>
            Draw sub-area
          </Button>
        }
      />
      <CardBody>
        {subAreas.length === 0 ? (
          <p className={styles.empty}>Nothing drawn yet. Draw a sub-area, then put it in a group.</p>
        ) : (
          <ul className={styles.list}>
            {subAreas.map((subArea) => (
              <li key={subArea.id} className={cn(styles.row, subArea.id === selectedId && styles.rowSelected)}>
                <span
                  className={cn(styles.swatch, toneClassForGroup(groups, subArea.groupId))}
                  aria-hidden="true"
                />
                <button
                  type="button"
                  className={styles.rowMain}
                  onClick={() => onSelect(subArea)}
                  aria-pressed={subArea.id === selectedId}
                  aria-label={`Show ${subArea.name} on the map`}
                >
                  <span className={styles.rowName}>{subArea.name}</span>
                  <span className={styles.rowMeta}>
                    {subArea.groupName ?? 'No group — adds nothing'} · {AREA_FORMAT.format(subArea.areaKm2)} km²
                  </span>
                </button>
                <div className={styles.rowActions}>
                  <Button
                    size="sm"
                    variant="ghost"
                    iconLeft={<Pencil aria-hidden="true" />}
                    aria-label={`Edit ${subArea.name}`}
                    onClick={() => onEdit(subArea)}
                    disabled={disabled}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    iconLeft={<Trash2 aria-hidden="true" />}
                    aria-label={`Delete ${subArea.name}`}
                    onClick={() => onDelete(subArea)}
                    disabled={disabled}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  )
}
